import type { LogicScope } from '../logic/types'

/**
 * PRD-026 v0.2 Definitions (North Star v2.7 principle 7): the partner-owned, versioned values a skill needs from the
 * client, held against the skill's declared parameter schema. Logic (Gallop-owned method) and Definitions
 * (partner-owned facts) are two objects with one ownership line between them; a partner never writes procedure,
 * because the only thing a partner can write is a value for a key the skill declared.
 *
 * The parameter-schema field names and the key-path grammar are one-way (PRD-026 §7 row 12; ADR 0051). The citation
 * grammar `def:<slug>@<version_no>#<key>` is one-way (§7 row 11).
 *
 * Prior art (PRD-026 §5a): Terraform module `variable` blocks (typed, described, defaulted, validated at the
 * boundary); Salesforce managed packages + customer settings; Ansible role vars precedence; QBO account mapping
 * screens (a picker over the company's own chart).
 */

export type DefinitionScope = LogicScope
export const DEFINITION_SCOPES: ReadonlyArray<DefinitionScope> = ['firm', 'client', 'file']

export type ParameterType = 'account_token' | 'text' | 'notes' | 'decimal' | 'integer' | 'percent' | 'date' | 'boolean' | 'enum' | 'party' | 'table'
export const PARAMETER_TYPES: ReadonlyArray<ParameterType> = ['account_token', 'text', 'notes', 'decimal', 'integer', 'percent', 'date', 'boolean', 'enum', 'party', 'table']

/** A parameter key or a table column name: lower-case, digits, underscores, 1 to 64 chars, letter first. */
export const PARAMETER_KEY_RE = /^[a-z][a-z0-9_]{0,63}$/
/** A table row key after slugifying the `row_key` cell (the Logic slug grammar). */
export const ROW_KEY_RE = /^[a-z0-9][a-z0-9-]{0,63}$/
/** A key path: `<param>` or `<param>.<row>`; the citation fragment. */
export const KEY_PATH_RE = /^[a-z][a-z0-9_]{0,63}(?:\.[a-z0-9][a-z0-9-]{0,63})?$/

export interface ColumnSpec {
  readonly key: string
  readonly label: string
  readonly type: Exclude<ParameterType, 'table' | 'notes'>
  readonly required: boolean
  readonly enum?: ReadonlyArray<string> | undefined
}

/**
 * How Gallop **suggests** a value from the file's own structure when the partner has not set one yet (stream L's
 * `resolve` hints, kept as schema metadata after ADR 0052 made the composer the master): the accounts snapshot
 * (`account_by_type`, `account_by_name`: exactly one active match or nothing) or a pinned Reference-table cell
 * (`logic_row`). Used at Definitions object creation to prefill the draft (`createDefinition` at file scope) and by
 * the seeds; never at spawn: an effective skill takes values from published Definitions and the skill's defaults only
 * (PRD-026 §3.13: the partner owns the definition, Gallop proposes it).
 */
export type ParameterDerive
  = | { readonly kind: 'account_by_type', readonly accountType: string, readonly accountSubType?: string | undefined }
    | { readonly kind: 'account_by_name', readonly pattern: string }
    | { readonly kind: 'logic_row', readonly slug: string, readonly row: string }

export interface ParameterSpec {
  readonly key: string
  readonly label: string
  readonly help?: string | undefined
  readonly type: ParameterType
  readonly required: boolean
  readonly default?: unknown
  /** `type: 'enum'`: the allowed values. */
  readonly enum?: ReadonlyArray<string> | undefined
  /** `type: 'table'`: the typed columns; the `row_key` column comes first in the editor and keys each row. */
  readonly columns?: ReadonlyArray<ColumnSpec> | undefined
  readonly row_key?: string | undefined
  /** Optional (additive, 2026-09-19): how a draft is prefilled from the file when the partner has not set the key. */
  readonly derive?: ParameterDerive | undefined
}

/** What a skill declares it needs from the client (PRD-026 §3.14). One per skill; `slug` is the Definitions object's slug. */
export interface ParameterSchema {
  readonly slug: string
  readonly title: string
  readonly version: number
  readonly parameters: ReadonlyArray<ParameterSpec>
}

/** The stored shape of a version's (or a draft's) values: `{ v: 1, values: { <key>: <value> } }`. */
export interface DefinitionValues {
  readonly v: 1
  readonly values: Readonly<Record<string, unknown>>
}

export type DefinitionKeyKind = 'value' | 'row'

/** One row of the key index (PRD-026 §6.6 `definition_key`): the citation target `#<key>`. */
export interface DefinitionKey {
  readonly key: string
  readonly kind: DefinitionKeyKind
  /** The parameter label, or the row key. */
  readonly label: string
  /** ≤ 240 chars; the rendered value, the default citation quote. */
  readonly excerpt: string
  /** 1-based: schema order, then row order. */
  readonly position: number
}

export type DefinitionProblemRule = 'definition.key_unknown' | 'definition.key_required' | 'definition.value_invalid' | 'definition.row_duplicate' | 'definition.values_unknown_version'

export interface DefinitionProblem {
  readonly rule: DefinitionProblemRule
  /** The key path the problem is about (`clearing_account`, `vendor_rules.rule-041`, `vendor_rules[3].action`); empty for a shape problem. */
  readonly key: string
  readonly detail: string
}

export interface DefinitionValidation {
  readonly ok: boolean
  readonly problems: ReadonlyArray<DefinitionProblem>
}

/** A candidate row for resolution (PRD-026 §3.4 reused): one object with one version already picked for `as_of`. */
export interface DefinitionCandidate {
  readonly def_id: string
  readonly slug: string
  readonly title: string
  readonly schema_version: number
  readonly scope: DefinitionScope
  readonly version_id: string
  readonly version_no: number
  readonly hash: string
  readonly effective_from: string
  readonly published_at: string
  readonly keys: ReadonlyArray<Pick<DefinitionKey, 'key' | 'kind' | 'label' | 'excerpt'>>
  readonly values?: DefinitionValues | undefined
}

/** One row of the pinned set (`GET /definitions/resolve`, `docs/api/prd026-definitions-source-contract.md`). */
export interface ResolvedDefinition extends DefinitionCandidate {
  readonly inherited_from: 'firm' | 'client' | null
}

export interface DefinitionResolveResponse {
  readonly v: 1
  readonly scope: { readonly account_id: string, readonly cluster_id: string | null, readonly file_id: string | null }
  readonly as_of: string
  readonly resolved_at: string
  readonly definitions: ReadonlyArray<ResolvedDefinition>
}

/** A parsed `def:<slug>@<version_no>#<key>` citation (PRD-026 §3.16). */
export interface DefinitionCitationRef {
  readonly slug: string
  readonly version_no: number
  readonly key: string
}

export type DefinitionCitationProblem = 'definition.citation_malformed' | 'definition.citation_not_pinned' | 'definition.key_unknown'

/** The stored citation object on a Review card for `kind = definition`. */
export interface DefinitionCitation {
  readonly kind: 'definition'
  readonly ref: string
  readonly def_id: string
  readonly version_id: string
  readonly key: string
  readonly quote?: string | undefined
}

export const DEFINITION_VALUES_MAX_CHARS = 524288
export const DEFINITION_EXCERPT_MAX_CHARS = 240
export const DEFINITION_TEXT_MAX_CHARS = 200
export const DEFINITION_NOTES_MAX_CHARS = 4000
